import { useEffect, useRef, useState, type RefObject } from 'react';

export interface UseAnchorVisibilityOptions {
  rootMargin?: string;
  enterThreshold?: number;
  exitThreshold?: number;
  debounceMs?: number;
}

export interface UseAnchorVisibilityResult {
  isAnchorVisible: boolean;
}

const DEFAULT_ENTER_THRESHOLD = 0.01;
const DEFAULT_EXIT_THRESHOLD = 0;
const DEFAULT_DEBOUNCE_MS = 80;

export function useAnchorVisibility(
  anchorRef: RefObject<Element | null>,
  options: UseAnchorVisibilityOptions = {}
): UseAnchorVisibilityResult {
  const {
    rootMargin = '0px',
    enterThreshold = DEFAULT_ENTER_THRESHOLD,
    exitThreshold = DEFAULT_EXIT_THRESHOLD,
    debounceMs = DEFAULT_DEBOUNCE_MS,
  } = options;

  const [isAnchorVisible, setIsAnchorVisible] = useState<boolean>(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestVisibleRef = useRef<boolean>(true);

  useEffect(() => {
    const element = anchorRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const thresholds = Array.from(
      new Set([0, exitThreshold, enterThreshold, 1].filter((value) => value >= 0 && value <= 1))
    ).sort((a, b) => a - b);

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;

        const ratio = entry.intersectionRatio;
        const currentlyVisible = latestVisibleRef.current;
        let nextVisible = currentlyVisible;

        if (currentlyVisible && ratio <= exitThreshold) {
          nextVisible = false;
        } else if (!currentlyVisible && ratio >= enterThreshold) {
          nextVisible = true;
        }

        if (nextVisible === currentlyVisible) return;

        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
          latestVisibleRef.current = nextVisible;
          setIsAnchorVisible(nextVisible);
        }, debounceMs);
      },
      { rootMargin, threshold: thresholds }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [anchorRef, rootMargin, enterThreshold, exitThreshold, debounceMs]);

  return { isAnchorVisible };
}
